import { Component, ContentChild, EventEmitter, Input, Output, TemplateRef } from '@angular/core';

import { Observable, Subject, combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { SubjectRelay } from '../../commands/subject-relay.class';

export type MultiExtendedFilterableSelectReinitData = {
	list: any[] | undefined;
}

class WrappedItem {
	constructor(
		public index: number, //
		public entity: any
	) {
	}
}

@Component({
	selector: 'multi-extended-filterable-select',
	templateUrl: 'multi-extended-filterable-select.html',
	styleUrls: ['multi-extended-filterable-select.css']
})
export class MultiExtendedFilterableSelectComponent {
	selections: boolean[] = [];

	// Item encapsule pour le selector interne
	wrappedItems: WrappedItem[] = [];
	
	// La fonction a utiliser pour verifier sur un -wrappedItem- appartient a une categorie
	wrappedItemCategoryChecker = (wrappedItem: WrappedItem, category: any) => this.categoryChecker(wrappedItem.entity, category);

	// Fonction a utiliser pour obtenir le libelle d'un item encapsule
	getWrappedItemLabel = (wrappedItem: WrappedItem) => this.labelGetter(wrappedItem.entity);

	showSelect: boolean = true;

	// Item actuellement selectionne dans le select
	selectedItem: any = null;
	
	// Evenements internes de changements externes pouvant amener a un changement
	preselectionFunctionSubject = new Subject<void>();
	itemListSubject = new Subject<void>();

	constructor() {
		// console.log("MEFS.constructor - INIT");
		
		let reinitEvent$ = this.reinitSubject.pipe(
			tap(
				(initData) => {
					// console.log("MEFS reinitEvent$ triggered");
					
					if (initData && initData.list != undefined) {
						this.itemList = [...initData.list];
						this.updateWrappedItems();
					}
				}
			),
			map(
				(_initData) => {}
			)
		)
		
		let preselectionFunctionEvent$ = this.preselectionFunctionSubject.pipe(
			tap(
				() => {
					// console.log("MEFS preselectionFunctionEvent$ triggered");
				}
			)
		);
		
		let itemListEvent$ = this.itemListSubject.pipe(
			tap(
				() => {
					// console.log("MEFS itemListEvent$ triggered");
				}
			)
		);
		
		combineLatest([reinitEvent$, preselectionFunctionEvent$, itemListEvent$]).subscribe(
			(_data) => {
				this.doPreselection()
			}
		);
		
		this.preselectionFunctionSubject.next();
		this.reinitSubject.next();
	}
	
	@ContentChild(TemplateRef) notesTemplate!: TemplateRef<any>;

	@Input() id!: string;
	@Input() disabled = false;
	@Input() maxNumberOfSelections: number | null = null;
	
	@Input() fieldClass: string = 'col-md-12';
	
	private isItemPreselected: (item: any) => boolean = (_item: boolean) => {
		// console.log("XXX: MEFS default isItemPreselected called");

		return false;	
	}
	@Input() set preselectionFunction(lambda :(item: any) => boolean) {
		// console.log("XXX: MEFS.set preselectionFunction(...)");

		this.isItemPreselected = lambda;
		this.preselectionFunctionSubject.next();
	}

	itemList: any[] = [];
	@Input() set list(values: any[]) {
		this.itemList = [...values];
		this.updateWrappedItems();
		this.itemListSubject.next();
	}
	get list(): any[] {
		return this.itemList;
	}

	// La fonction a utiliser pour obtenir le libelle d'un item
	@Input() labelGetter!: (item: any) => string;

	// La liste des categories pour l'extended-filterable-select interne
	@Input() categoryList: any[] = []

	// La fonction a utiliser pour obtenir le libelle d'une categorie
	@Input() categoryLabelGetter!: (item: any) => string;

	// Le label de l'option "tous" dans la liste des categories (null = pas d'option "Tous")
	@Input() wildcardCategoryLabel: string | null = null;

	// La fonction a utiliser pour determiner si un item appartient a une categorie	
	@Input() categoryChecker!: (item: any, category: any) => boolean;
	
	private reinitSubject = new SubjectRelay<MultiExtendedFilterableSelectReinitData | void>();
	@Input() set reinitCommands(obs: Observable<MultiExtendedFilterableSelectReinitData | void> | null | undefined) {
		this.reinitSubject.registerSource(obs);
	}

	// Evenements de changement de selection
	@Output() selectedItemsChange = new EventEmitter<any[]>();
	
	private doPreselection() {
		// console.log("XXX: MEFS.doPreselection() - liste options:", this.itemList);
		// console.log("XXX: MEFS.doPreselection() - previously selected:", this.selections);
		
		let selections: boolean[] = new Array(this.itemList.length);
		let changesHappened: boolean = this.itemList.length != this.selections.length;
		for (let i = 0; i < this.itemList.length; i++) {
			const item = this.itemList[i];
			const selected = this.isItemPreselected(item);
			selections[i] = selected;
			changesHappened = changesHappened || this.selections[i] != selected;
		}

		// console.log("XXX: MEFS.doPreselection() - new selected:", selections);
		// console.log("XXX: MEFS.doPreselection() - changesHappened", changesHappened);
		
		if (changesHappened) {
			this.selections = selections;
			this.updateWrappedItems();
			this.emitSelectionChange();
		}
	}

	onSelectItem(wrappedItem: WrappedItem | null) {
		this.selectedItem = wrappedItem;

		if (wrappedItem == null) {
			// Le composant de selection envoie null quand on deselectionne l'item dans celui-ci
			// Dans ce cas on ne fait rien ici
			return;
		}
		
		this.selections[wrappedItem.index] = true;
		this.updateWrappedItems();
		this.emitSelectionChange();
	}

	onDeselectItem(index: number) {
		// console.log("XXX: MEFS.onDeselectItem()");

		if (!this.disabled) {
			this.selections[index] = false;
			this.updateWrappedItems();
			this.emitSelectionChange();
		}
	}

	onSelectAll() {
		// console.log("XXX: MEFS.onSelectAll()");

		if (!this.disabled) {
			this.selections.fill(true);
			this.updateWrappedItems();
			this.emitSelectionChange();
		}
	}

	onDeselectAll() {
		// console.log("XXX: MEFS.onDeselectAll()");

		if (!this.disabled) {
			this.selections.fill(false);
			this.updateWrappedItems();
			this.emitSelectionChange();
		}
	}
	
	shouldShowAddOrRemoveAll() {
		if (this.wrappedItems.length) {
			if (this.maxNumberOfSelections) {
				return this.maxNumberOfSelections > 1;
			}
			return true;
		}
		return false;
	}

	private updateWrappedItems() {
		this.wrappedItems = this.itemList //
			.map(
				(item: any, index: number) => {
					return new WrappedItem(index, item);
				}
			) //
			.filter(
				(wrappedItem: WrappedItem) => !this.selections[wrappedItem.index]
			)
		;
		this.showSelect = this.maxNumberOfSelections ? this.selections.filter($ => $).length < this.maxNumberOfSelections : this.wrappedItems.length > 0;
		this.selectedItem = -1;
		setTimeout(() => {
			this.selectedItem = null;
		}, 100);
	}

	private emitSelectionChange() {
		// console.log("XXX: MEFs.emitSelectionChange()");
		
		const selection = this.itemList //
			.filter(
				(_item: any, index: number) => this.selections[index]
			);
		this.selectedItemsChange.emit(selection);
	}

	doNothing() {
	}
}